// AIPanel.js - Config di-load SEKALI di sini, tidak hilang saat panel buka/tutup
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { loadConfigFromServer, getDefaultConfig } from './aiConfig';
import AIChat, { getDashboardContext } from './AIChat';
import AIHistorySidebar from './AIHistorySidebar';
import { ROUTE_META } from '../../routesMeta';
import { dashboardKeyForPath } from '../../config/dashboardKeys';
import { api } from '../../context/AuthContext';
import { useLang } from '../../context/LanguageContext';
import { toChatMessages } from './sessionResume';

const MIN_WIDTH = 300;
const MAX_WIDTH = 700;

// Lebar sidebar riwayat saat maximize. Tetap, tidak bisa di-drag: satu handle
// resize sudah ada untuk panel, dan menambah handle kedua membuat keduanya
// lebih mudah tertukar daripada berguna.
const HISTORY_W = 260;

export default function AIPanel({ open, onClose, defaultWidth = 380, activeKey, activeLabel, pageContext }) {
  const location          = useLocation();
  const { lang }          = useLang();
  const [width, setWidth] = useState(defaultWidth);
  const isDragging        = useRef(false);
  const startX            = useRef(0);
  const startW            = useRef(0);

  // Config di-load SEKALI di sini dan tetap ada selama session
  const [config, setConfig]           = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [maximized, setMaximized]     = useState(false);
  const suppressResetRef              = useRef(false); // dipakai auto-detect di AIChat (opsional)

  // ── Sidebar riwayat ────────────────────────────────────────────────────────
  // `resume` + `chatKey` adalah mekanisme melanjutkan sesi. Prop `resume` di
  // AIChat hanya dibaca di initializer useState/useRef, jadi menggantinya saja
  // TIDAK memuat apa pun — AIChat harus benar-benar di-mount ulang. `chatKey`
  // yang memaksa itu. Pola yang sama sudah dipakai AICoding.js.
  //
  // Yang TIDAK ikut hilang saat remount: `config` dan `maximized`, karena
  // keduanya hidup di komponen ini. Yang hilang: draft input dan gambar yang
  // sudah dilampirkan tapi belum dikirim.
  const [resume, setResume]           = useState(null);
  const [chatKey, setChatKey]         = useState('ai-chat-persistent');
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [historyTick, setHistoryTick] = useState(0);
  const [picking, setPicking]         = useState(false);

  // Nama dashboard untuk memfilter sidebar. WAJIB lewat getDashboardContext():
  // kolom `dashboard` di ai_chat_history menyimpan NAMA TAMPILAN hasil fungsi
  // itu (mis. "SAP Solution Manager"), bukan label rute dan bukan dashboardKey.
  // Memakai activeLabel mentah akan meleset untuk setiap halaman yang punya
  // entri di DASH_HINTS, dan gejalanya cuma sidebar yang kosong.
  const dashKey       = activeKey || dashboardKeyForPath(location.pathname);
  const dashboardName = getDashboardContext(dashKey, activeLabel)?.name || '';

  // Reset maximize saat panel ditutup → buka lagi kembali sebagai side panel.
  useEffect(() => { if (!open) setMaximized(false); }, [open]);

  // ── Undock / floating bubble ───────────────────────────────────────────────
  // Mode mengapung: posisi & ukuran disimpan per browser (localStorage).
  // Drag lewat header AIChat (elemen ber-atribut data-ai-drag), resize lewat
  // pegangan di pojok kanan-bawah.
  const FLOAT_KEY = 'ai_panel_float_v1';
  const loadFloat = () => {
    try {
      const s = JSON.parse(localStorage.getItem(FLOAT_KEY) || 'null');
      if (s && typeof s.x === 'number') return s;
    } catch (e) {}
    const w = 420, h = Math.min(640, window.innerHeight - 120);
    return { floating: false, x: window.innerWidth - w - 24, y: window.innerHeight - h - 24, w, h };
  };
  const [fl, setFl] = useState(loadFloat);
  const floating = fl.floating && !maximized;
  const flDrag = useRef(null); // { mode:'move'|'resize', sx, sy, ox, oy, ow, oh }
  const [flActive, setFlActive] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(FLOAT_KEY, JSON.stringify(fl)); } catch (e) {}
  }, [fl]); // eslint-disable-line

  const clampFl = (s) => {
    const W = window.innerWidth, H = window.innerHeight;
    const w = Math.min(Math.max(300, s.w), W - 16);
    const h = Math.min(Math.max(360, s.h), H - 16);
    const x = Math.min(Math.max(8, s.x), W - w - 8);
    const y = Math.min(Math.max(8, s.y), H - h - 8);
    return { ...s, x, y, w, h };
  };

  const toggleFloat = useCallback(() => {
    setFl(s => clampFl({ ...s, floating: !s.floating }));
  }, []); // eslint-disable-line

  const onFloatMouseDown = useCallback((e) => {
    if (!floating || e.button !== 0) return;
    const resize = e.target.closest?.('[data-ai-resize]');
    const drag   = e.target.closest?.('[data-ai-drag]');
    if (!resize && (!drag || e.target.closest('button,input,select,textarea,a'))) return;
    e.preventDefault();
    flDrag.current = { mode: resize ? 'resize' : 'move', sx: e.clientX, sy: e.clientY,
                       ox: fl.x, oy: fl.y, ow: fl.w, oh: fl.h };
    setFlActive(true);
  }, [floating, fl]);

  useEffect(() => {
    if (!flActive) return;
    const onMove = (e) => {
      const d = flDrag.current; if (!d) return;
      const dx = e.clientX - d.sx, dy = e.clientY - d.sy;
      setFl(s => clampFl(d.mode === 'move'
        ? { ...s, x: d.ox + dx, y: d.oy + dy }
        : { ...s, w: d.ow + dx, h: d.oh + dy, x: d.ox, y: d.oy }));
    };
    const onUp = () => { flDrag.current = null; setFlActive(false); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [flActive]); // eslint-disable-line

  // Jaga bubble tetap di layar saat window di-resize.
  useEffect(() => {
    const onR = () => setFl(s => clampFl(s));
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []); // eslint-disable-line

  // Pindah halaman → buang sesi yang sedang dilanjutkan. AIChat memang sudah
  // membersihkan transkripnya sendiri saat dashboardKey berubah, tapi `resume`
  // di sini akan tetap tersangkut dan sesi lama muncul lagi pada remount
  // berikutnya — di halaman yang salah.
  useEffect(() => {
    setResume(null);
    setActiveSessionId(null);
  }, [location.pathname]);

  useEffect(() => {
    loadConfigFromServer()
      .then(cfg => {
        setConfig(cfg || getDefaultConfig());
      })
      .catch(() => setConfig(getDefaultConfig()))
      .finally(() => setConfigLoading(false));
  }, []); // eslint-disable-line

  // AIChat melapor session_id aktifnya: saat mount, setelah pesan user pertama
  // tersimpan, setelah jawaban selesai, dan saat sesi di-reset.
  const handleSessionActivity = useCallback((sid) => {
    setActiveSessionId(sid);
    setHistoryTick(n => n + 1);
  }, []);

  const handlePickSession = useCallback(async (sessionId) => {
    if (picking || sessionId === activeSessionId) return;
    setPicking(true);
    try {
      const r = await api.get(`/api/ai/chat-history/session/${encodeURIComponent(sessionId)}`);
      setResume({ session_id: sessionId, messages: toChatMessages(r.data, lang) });
      // Key diturunkan dari session_id supaya memilih sesi yang sama dua kali
      // tidak memicu remount yang tidak perlu.
      setChatKey(`ai-chat-${sessionId}`);
      setActiveSessionId(sessionId);
    } catch (e) {
      console.error('[AIPanel] gagal memuat sesi:',
        e?.response?.status || '', e?.response?.data?.error || e.message);
      alert(`Gagal memuat sesi: ${e?.response?.data?.error || e.message}`);
    } finally {
      setPicking(false);
    }
  }, [picking, activeSessionId, lang]);

  const handleNewChat = useCallback(() => {
    setResume(null);
    // Key unik, bukan konstanta: kalau chatKey kebetulan sudah bernilai sama,
    // React tidak me-remount apa pun dan transkrip lama tetap di layar.
    setChatKey(`ai-chat-new-${Date.now()}`);
    setActiveSessionId(null);
  }, []);

  // Resize handle
  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    isDragging.current = true;
    startX.current     = e.clientX;
    startW.current     = width;
  }, [width]);

  useEffect(() => {
    const onMove = (e) => {
      if (!isDragging.current) return;
      const delta    = startX.current - e.clientX;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startW.current + delta));
      setWidth(newWidth);
    };
    const onUp = () => { isDragging.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape' && open) onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  return (
    <div
      onMouseDown={onFloatMouseDown}
      className={floating
        ? 'bg-slate-50 border border-slate-200 flex flex-col rounded-2xl shadow-2xl'
        : 'flex-shrink-0 bg-slate-50 border-l border-slate-200 flex flex-col relative'}
      style={
        maximized
          ? { position: 'fixed', inset: 0, width: '100vw', zIndex: 50, overflow: 'hidden' }
          : floating
          ? { position: 'fixed', left: fl.x, top: fl.y, width: fl.w, height: fl.h,
              zIndex: 60, overflow: 'hidden', display: open ? 'flex' : 'none',
              userSelect: flActive ? 'none' : undefined }
          : {
              width: open ? width : 0,
              minWidth: open ? MIN_WIDTH : 0,
              transition: isDragging.current ? 'none' : 'width 0.3s cubic-bezier(0.4,0,0.2,1)',
              overflow: 'hidden',
            }
      }
    >
      {open && (
        <>
          {/* Resize handle — hanya di mode side panel */}
          {floating && (
            <div data-ai-resize="1" title="Drag untuk resize"
              className="absolute right-0 bottom-0 w-4 h-4 cursor-nwse-resize z-50"
              style={{ background: 'linear-gradient(135deg, transparent 50%, #94a3b8 50%)', borderBottomRightRadius: 16 }} />
          )}
          {!maximized && !floating && (
            <div
              onMouseDown={onMouseDown}
              className="absolute left-0 top-0 h-full w-1.5 cursor-col-resize z-50 group"
              title="Drag untuk resize"
            >
              <div className="h-full w-full bg-transparent group-hover:bg-brand-400 transition-colors duration-150" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-12 bg-slate-300 group-hover:bg-brand-500 rounded-full transition-colors duration-150" />
            </div>
          )}

          {/* Maximize → dua kolom: riwayat di kiri, chat di kanan.
              Side panel sempit (min 300px) tidak muat dibagi dua, jadi sidebar
              memang HANYA muncul saat maximize. */}
          <div className="flex flex-row h-full min-h-0 w-full">
            {maximized && (
              <div className="flex-shrink-0 h-full min-h-0" style={{ width: HISTORY_W }}>
                <AIHistorySidebar
                  dashboardName={dashboardName}
                  activeSessionId={activeSessionId}
                  refreshToken={historyTick}
                  onPick={handlePickSession}
                  onNewChat={handleNewChat}
                  // Panel ini bukan rute, jadi tidak bisa jadi tujuan tab baru.
                  // Tujuannya AI Workspace — versi halaman penuh dari chat yang
                  // sama, membaca sesi dari `?session=`. Konsekuensi yang
                  // disengaja: tab baru terbuka pada target "Umum", bukan
                  // dashboard tempat panel dibuka. Transkripnya tetap utuh; yang
                  // berbeda hanya konteks data untuk pertanyaan BERIKUTNYA.
                  sessionHref={sid => `/dashboard/ai-workspace?session=${encodeURIComponent(sid)}`}
                  lang={lang}
                />
              </div>
            )}

            {/* Chat — config diteruskan sebagai prop, tidak di-load ulang */}
            <div
              className="flex flex-col h-full min-h-0"
              style={maximized || floating ? { flex: 1, minWidth: 0 } : { width, minWidth: MIN_WIDTH }}
            >
              {configLoading ? (
                <div className="flex items-center justify-center h-full gap-2 text-slate-400">
                  <div className="w-4 h-4 border-2 border-brand-400 border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs">Memuat konfigurasi...</span>
                </div>
              ) : (
                <AIChat
                  key={chatKey}
                  onClose={onClose}
                  currentPath={location.pathname}
                  activeKey={activeKey}
                  activeLabel={activeLabel}
                  pageContext={pageContext}
                  initialConfig={config}
                  onConfigChange={setConfig}
                  maximized={maximized}
                  onToggleMaximize={() => setMaximized(m => !m)}
                  floating={floating}
                  onToggleFloat={toggleFloat}
                  resolveDashboardLabel={(k) => (ROUTE_META.find(r => r.key === k)?.label || k)}
                  suppressResetRef={suppressResetRef}
                  resume={resume}
                  onSessionActivity={handleSessionActivity}
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
